import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';
import { formatPhone } from '../../shared/phone';
import { formatShortDate } from '../../shared/time';
import {
  IconMail,
  IconMessage,
  IconPhone,
  StatusDot,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeletonRows,
  UnreadMarker,
} from '../../ui/v2';
import type { InquiryKindConfig } from './kind';
import {
  type InquiryRecord,
  type SortKey,
  STAGE_LABEL,
  STAGE_TONE,
  formatEventDate,
  formatTime,
  isOpenStage,
  nextAction,
  occasionLabel,
  relativeEventLabel,
  titleFor,
} from './model';

interface CommonProps {
  config: InquiryKindConfig;
  items: InquiryRecord[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  loading?: boolean;
}

/** ↑/↓ or j/k move focus between rows; with a record open they also open it. */
function useRowKeys(
  items: InquiryRecord[],
  selectedId: number | null,
  onSelect: (id: number) => void,
) {
  return (e: React.KeyboardEvent<HTMLElement>, id: number) => {
    const idx = items.findIndex((i) => i.id === id);
    const step =
      e.key === 'ArrowDown' || e.key === 'j'
        ? 1
        : e.key === 'ArrowUp' || e.key === 'k'
          ? -1
          : 0;
    if (!step) return;
    e.preventDefault();
    // With a record open the page-level handler moves the selection.
    e.stopPropagation();
    const nextIdx = Math.min(Math.max(idx + step, 0), items.length - 1);
    const target = items[nextIdx];
    if (!target || nextIdx === idx) return;
    (e.currentTarget.parentElement?.children[nextIdx] as HTMLElement)?.focus();
    if (selectedId != null) onSelect(target.id);
  };
}

const Missing: React.FC = () => <span className="text-v2-fg-4">—</span>;

const DateCell: React.FC<{ e: InquiryRecord }> = ({ e }) => {
  if (!e.eventDate && !e.eventDateRaw) return <Missing />;
  const when = relativeEventLabel(e.eventDate);
  const upcoming =
    isOpenStage(e.stage) &&
    when != null &&
    !when.endsWith('ago') &&
    when !== 'yesterday';
  return (
    <span className="flex min-w-0 flex-col leading-4">
      <span className="truncate">
        <span
          className={clsx(!e.eventDate && 'text-v2-fg-2')}
          title={!e.eventDate ? (e.eventDateRaw ?? undefined) : undefined}
        >
          {formatEventDate(e.eventDate, e.eventDateRaw, { withYear: true })}
        </span>
        {e.eventTime && e.eventDate && (
          <span className="ml-1.5 text-v2-fg-3">{formatTime(e.eventTime)}</span>
        )}
      </span>
      {when && (
        <span
          className={clsx(
            'text-v2-11',
            upcoming ? 'text-v2-warning' : 'text-v2-fg-4',
          )}
        >
          {when}
        </span>
      )}
    </span>
  );
};

const channelIcon = (label: string) => {
  const verb = label.toLowerCase();
  if (verb.startsWith('email')) return <IconMail size={14} />;
  if (verb.startsWith('text')) return <IconMessage size={14} />;
  if (verb.startsWith('call')) return <IconPhone size={14} />;
  return null;
};

const EmailCell: React.FC<{ e: InquiryRecord }> = ({ e }) => {
  const s = e.coordinatorEmail.status;
  if (s === 'sent') return <Tag tone="success">Sent</Tag>;
  if (s === 'failed') return <Tag tone="danger">Failed</Tag>;
  return <Tag tone="neutral">{s === 'processing' ? 'Sending' : 'Pending'}</Tag>;
};

const SKELETON_COLS = [
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  'hidden lg:table-cell',
  'hidden xl:table-cell',
  undefined,
];

export const InquiryTable: React.FC<
  CommonProps & {
    sortKey: SortKey;
    sortDir: 'asc' | 'desc';
    onSort: (key: SortKey) => void;
  }
> = ({
  config,
  items,
  selectedId,
  onSelect,
  loading,
  sortKey,
  sortDir,
  onSort,
}) => {
  const onKey = useRowKeys(items, selectedId, onSelect);
  const th = (
    key: SortKey,
    label: string,
    opts: Partial<React.ComponentProps<typeof TH>> = {},
  ) => (
    <TH
      sortable
      active={sortKey === key}
      dir={sortDir}
      onSort={() => onSort(key)}
      {...opts}
    >
      {label}
    </TH>
  );
  return (
    <Table fixed className="min-w-[960px]">
      <THead>
        <tr>
          {th('guest', 'Guest', { width: 220 })}
          {th('occasion', config.labels.occasion, { width: 168 })}
          {th('date', config.labels.date, { width: 188 })}
          <TH width={88}>{config.labels.guests}</TH>
          <TH width={120}>Stage</TH>
          <TH>Next action</TH>
          <TH className="hidden lg:table-cell" width={156}>
            Phone
          </TH>
          <TH className="hidden xl:table-cell" width={112}>
            Team email
          </TH>
          {th('received', 'Received', { align: 'right', width: 112 })}
        </tr>
      </THead>
      <TBody>
        {loading && items.length === 0 ? (
          <TableSkeletonRows cols={SKELETON_COLS} size="lg" />
        ) : (
          items.map((e) => {
            const na = nextAction(e);
            const actionIcon = channelIcon(na.label);
            const isNew = e.stage === 'new';
            return (
              <TR
                key={e.id}
                size="lg"
                selected={e.id === selectedId}
                unread={isNew}
                onActivate={() => onSelect(e.id)}
                onKeyDown={(ev) => onKey(ev, e.id)}
              >
                <TD
                  size="lg"
                  className={isNew ? 'font-semibold' : 'font-medium'}
                >
                  {isNew && <UnreadMarker />}
                  <span className="flex min-w-0 flex-col leading-4">
                    <span className="truncate">
                      {e.guestName ? (
                        titleFor(e)
                      ) : e.phone ? (
                        <span className="v2-num font-normal text-v2-fg-2">
                          {formatPhone(e.phone)}
                        </span>
                      ) : (
                        <span className="font-normal text-v2-fg-3">
                          Unknown caller
                        </span>
                      )}
                    </span>
                    {e.email && (
                      <span className="truncate text-v2-12 font-normal text-v2-fg-3">
                        {e.email}
                      </span>
                    )}
                  </span>
                </TD>
                <TD size="lg" truncate className="text-v2-fg-2">
                  {e.occasion ? occasionLabel(e.occasion, '') : <Missing />}
                </TD>
                <TD size="lg" num>
                  <DateCell e={e} />
                </TD>
                <TD size="lg" num truncate>
                  {e.guestCount ??
                    (e.guestCountRaw ? (
                      <span
                        title={`Heard as “${e.guestCountRaw}”`}
                        className="text-v2-fg-3"
                      >
                        {e.guestCountRaw}
                      </span>
                    ) : (
                      <Missing />
                    ))}
                </TD>
                <TD size="lg">
                  <Tag tone={STAGE_TONE[e.stage]}>{STAGE_LABEL[e.stage]}</Tag>
                </TD>
                <TD
                  size="lg"
                  truncate
                  className={clsx(
                    'text-v2-12',
                    na.urgency === 'now' ? 'text-v2-fg' : 'text-v2-fg-3',
                  )}
                >
                  {na.label ? (
                    <span className="inline-flex min-w-0 items-center gap-1.5">
                      {actionIcon && (
                        <span className="shrink-0 text-v2-fg-4">
                          {actionIcon}
                        </span>
                      )}
                      <span className="truncate">{na.label}</span>
                    </span>
                  ) : (
                    <Missing />
                  )}
                </TD>
                <TD
                  size="lg"
                  num
                  truncate
                  className="hidden text-v2-12 text-v2-fg-2 lg:table-cell"
                >
                  {e.phone ? (
                    <span className="inline-flex items-center gap-1.5">
                      <IconPhone size={14} className="text-v2-fg-4" />
                      {formatPhone(e.phone)}
                    </span>
                  ) : (
                    <Missing />
                  )}
                </TD>
                <TD size="lg" className="hidden xl:table-cell">
                  <EmailCell e={e} />
                </TD>
                <TD
                  size="lg"
                  num
                  align="right"
                  className="whitespace-nowrap text-v2-12 text-v2-fg-3"
                >
                  {formatShortDate(e.createdAt)}
                </TD>
              </TR>
            );
          })
        )}
      </TBody>
    </Table>
  );
};

/** Two-line rows beside an open record. */
export const InquiryCompactList: React.FC<CommonProps> = ({
  config,
  items,
  selectedId,
  onSelect,
  loading,
}) => {
  const onKey = useRowKeys(items, selectedId, onSelect);
  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (selectedId == null) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-id="${selectedId}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedId]);

  if (loading && items.length === 0) {
    return (
      <ul aria-label="Loading inquiries" className="flex flex-col">
        {Array.from({ length: 8 }).map((_, i) => (
          <li
            key={i}
            className="flex h-[52px] flex-col justify-center gap-2 border-b border-v2-line/80 px-4"
          >
            <span
              className="v2-skeleton block h-3"
              style={{ width: `${50 + ((i * 17) % 40)}%` }}
            />
            <span
              className="v2-skeleton block h-2.5"
              style={{ width: `${30 + ((i * 11) % 40)}%` }}
            />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul
      ref={listRef}
      role="listbox"
      aria-label={`${config.recordLabel} inquiries`}
      className="flex flex-col"
    >
      {items.map((e) => {
        const selected = e.id === selectedId;
        const isNew = e.stage === 'new';
        return (
          <li
            key={e.id}
            data-id={e.id}
            role="option"
            aria-selected={selected}
            tabIndex={0}
            onClick={() => onSelect(e.id)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                onSelect(e.id);
              } else onKey(ev, e.id);
            }}
            className={clsx(
              'relative flex h-[52px] cursor-pointer flex-col justify-center gap-0.5 border-b border-v2-line/80 px-4 outline-none transition-colors',
              selected
                ? 'bg-v2-accent/[0.07]'
                : 'hover:bg-v2-sunken/70 focus-visible:bg-v2-sunken',
            )}
          >
            {isNew && <UnreadMarker />}
            <div className="flex items-center gap-2">
              <span
                className={clsx(
                  'min-w-0 flex-1 truncate text-v2-13',
                  isNew ? 'font-semibold' : 'font-medium',
                )}
              >
                {titleFor(e)}
              </span>
              {isNew && <span className="sr-only">New</span>}
              <span className="v2-num shrink-0 text-v2-11 text-v2-fg-3">
                {formatEventDate(e.eventDate, e.eventDateRaw)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-v2-12 text-v2-fg-3">
              <StatusDot tone={STAGE_TONE[e.stage]} className="h-1.5 w-1.5" />
              <span className="min-w-0 flex-1 truncate">
                {occasionLabel(e.occasion, config.occasionFallback)}
                {e.guestCount ? ` · ${e.guestCount} guests` : ''}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
